import { useCallback, useEffect, useRef, useState } from "react";
import {
  createInteractiveSlTpLineOverlay,
  InteractiveSlTpLineData,
  registerInteractiveSlTpLineOverlay,
  removeInteractiveSlTpLineOverlay,
} from "@/lib/chart-helpers/custom-overlays/interactiveSlTpLineOverlay";
import { formatOrderType } from "@/vybe-trader-client/trade-history/types";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades/stores/useOpenedTradesStore";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { calculateDefaultTpPrice } from "@/lib/chart-helpers/calculateSLTPValues";
import { getTradeOpenTime } from "@/lib/chart-helpers/trade-overlay/utils";

interface UseInteractiveTpNewProps {
  chartInstanceRef: React.MutableRefObject<any>;
  ticket: string | null;
  tpEnabled: boolean;
  editableTpValue: number;
  currentTpValue?: number;
  autoSlTpModify?: boolean;
  onPriceUpdate?: (price: number) => void;
  onModify?: (type: "TP", price: number) => void;
  tradeData?: any;
  isPipsVisible?: boolean;
}

export const useInteractiveTpNew = ({
  chartInstanceRef,
  ticket,
  tpEnabled,
  editableTpValue,
  currentTpValue = 0,
  autoSlTpModify = false,
  onPriceUpdate,
  onModify,
  tradeData,
  isPipsVisible = false,
}: UseInteractiveTpNewProps) => {
  const [tpOverlayId, setTpOverlayId] = useState<string | null>(null);
  const tpOverlayIdRef = useRef<string | null>(null);
  const ticketRef = useRef<string | null>(null);
  const prevTicketRef = useRef<string | null>(null);

  const currentTrade = useOpenedTradesStore((state) =>
    ticket ? state.allTrades.find((t) => t.ticket.toString() === ticket) : null
  );

  const tradeSymbol = currentTrade?.symbol ?? "";
  const quoteForLevels = useSubscribedSymbolsStore((state) =>
    tradeSymbol ? state.byCurrency.get(tradeSymbol)?.quote : undefined,
  );

  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);

  /**
   * Removes TP interactive overlays from chart using latest overlay id ref (safe on unmount; no setState).
   * Pass `sweepTicket` when `ticketRef` already advanced so pane sweep still targets the old ticket.
   */
  const tearDownTpOverlayChartArtifacts = useCallback(
    (sweepTicket?: string | null) => {
      const chart = chartInstanceRef.current;
      const tid = sweepTicket ?? ticketRef.current;
      const id = tpOverlayIdRef.current;

      if (chart && id) {
        try {
          removeInteractiveSlTpLineOverlay(chart, id);
        } catch (error) {
          console.error(`Failed to remove TP overlay ${id}:`, error);
        }
      }
      tpOverlayIdRef.current = null;

      if (!chart || !tid) {
        return;
      }

      try {
        const overlayMap = chart.getOverlaysByPaneId?.();
        if (!overlayMap || !(overlayMap instanceof Map)) {
          return;
        }

        overlayMap.forEach((paneOverlays) => {
          if (!(paneOverlays instanceof Map)) {
            return;
          }

          paneOverlays.forEach((overlay, overlayId) => {
            if (!overlay?.extendData) {
              return;
            }
            const data = overlay.extendData;
            const ticketMatch =
              String(data.ticket) === String(tid) && data.type === "TP";
            if (ticketMatch) {
              try {
                chart.removeOverlay(overlayId);
              } catch (error) {
                console.error("Failed to remove TP overlay:", error);
              }
            }
          });
        });
      } catch (error) {
        console.error("Error during TP cleanup:", error);
      }
    },
    [chartInstanceRef]
  );

  const cleanup = useCallback(() => {
    tearDownTpOverlayChartArtifacts();
    setTpOverlayId(null);
  }, [tearDownTpOverlayChartArtifacts]);

  useEffect(() => {
    registerInteractiveSlTpLineOverlay();
  }, []);

  useEffect(() => {
    if (!ticket) {
      return;
    }

    const unsubscribe = useOpenedTradesStore.subscribe((state, prevState) => {
      const currentTrades = state.allTrades;
      const prevTrades = prevState.allTrades;

      const tradeExistsNow = currentTrades.some(
        (t) => t.ticket.toString() === ticket
      );
      const tradeExistedBefore = prevTrades.some(
        (t) => t.ticket.toString() === ticket
      );

      if (tradeExistedBefore && !tradeExistsNow) {
        cleanup();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [ticket, cleanup]);

  useEffect(() => {
    if (ticket && !currentTrade) {
      cleanup();
    }
  }, [ticket, currentTrade, cleanup]);

  useEffect(() => {
    const prev = prevTicketRef.current;
    if (prev !== null && prev !== ticket) {
      tearDownTpOverlayChartArtifacts(prev);
      setTpOverlayId(null);
    }
    prevTicketRef.current = ticket;
  }, [ticket, tearDownTpOverlayChartArtifacts]);

  const getDefaultTpPrice = () => {
    if (!currentTrade) return 0;
    return calculateDefaultTpPrice(currentTrade, {
      quote: quoteForLevels,
      digits: currentTrade.digits,
    });
  };

  const handlePriceChange = (type: "SL" | "TP", newPrice: number) => {
    if (type !== "TP") return;

    if (onPriceUpdate) {
      onPriceUpdate(newPrice);
    }

    const id = tpOverlayIdRef.current;
    if (id) {
      refreshOverlay(id, newPrice);
    }
  };

  const refreshOverlay = (overlayId: string, newPrice: number) => {
    if (!chartInstanceRef.current || !currentTrade) return;

    try {
      const originalTradeValue = currentTrade.takeProfit || 0;

      const openTime = getTradeOpenTime(currentTrade);
      chartInstanceRef.current.overrideOverlay({
        id: overlayId,
        extendData: {
          price: newPrice,
          type: "TP",
          tradeType: formatOrderType(currentTrade.orderType).toUpperCase() as
            | "BUY"
            | "SELL",
          ticket: currentTrade.ticket.toString(),
          openTime,
          originalPrice: originalTradeValue,
          currentTradeValue: currentTpValue,
          autoSlTpModify: autoSlTpModify,
          isFirstAddition: false,
          tradeData: tradeData,
          isPipsVisible: isPipsVisible,
        },
        points: [
          {
            timestamp: openTime,
            value: newPrice,
          },
        ],
      });
    } catch (error) {
      console.error(`Failed to refresh TP overlay:`, error);
    }
  };

  const handleBadgeClick = (
    type: "SL" | "TP",
    action: "remove" | "modify",
    price: number
  ) => {
    if (type !== "TP") return;

    if (action === "remove") {
      if (onPriceUpdate) {
        onPriceUpdate(0);
      }
    }

    if (onModify) {
      onModify("TP", action === "remove" ? 0 : price);
    }
  };

  const handleAutoModify = (type: "SL" | "TP", price: number) => {
    if (type !== "TP") return;

    if (onModify) {
      onModify("TP", price);
    }
  };

  const getCurrentAutoSlTpModify = () => {
    return autoSlTpModify;
  };

  const hasExistingTp = (currentTpValue ?? 0) > 0;

  useEffect(() => {
    if (!chartInstanceRef.current || !currentTrade) return;

    if (tpEnabled && !hasExistingTp) {
      if (!tpOverlayIdRef.current) {
        let initialTpPrice = editableTpValue;

        if (editableTpValue <= 0) {
          initialTpPrice = getDefaultTpPrice();

          if (onPriceUpdate) {
            onPriceUpdate(initialTpPrice);
          }
        }

        const tpLineData: InteractiveSlTpLineData = {
          price: initialTpPrice,
          type: "TP",
          tradeType: formatOrderType(currentTrade.orderType).toUpperCase() as
            | "BUY"
            | "SELL",
          ticket: currentTrade.ticket.toString(),
          openTime: getTradeOpenTime(currentTrade),
          originalPrice: currentTrade.takeProfit || 0,
          currentTradeValue: currentTpValue,
          autoSlTpModify: autoSlTpModify,
          isFirstAddition: (currentTrade.takeProfit || 0) === 0,
          tradeData: tradeData,
          isPipsVisible: isPipsVisible,
        };

        try {
          const overlayId = createInteractiveSlTpLineOverlay(
            chartInstanceRef.current,
            tpLineData,
            handlePriceChange,
            handleBadgeClick,
            handleAutoModify,
            getCurrentAutoSlTpModify
          );
          if (overlayId) {
            tpOverlayIdRef.current = overlayId;
            setTpOverlayId(overlayId);
          }
        } catch (error) {
          console.error("Failed to create TP overlay:", error);
        }
      } else {
        refreshOverlay(tpOverlayIdRef.current, editableTpValue);
      }
    } else {
      const existingId = tpOverlayIdRef.current;
      if (existingId) {
        removeInteractiveSlTpLineOverlay(chartInstanceRef.current, existingId);
        tpOverlayIdRef.current = null;
        setTpOverlayId(null);
      }
    }
  }, [
    tpEnabled,
    editableTpValue,
    currentTpValue,
    hasExistingTp,
    autoSlTpModify,
    isPipsVisible,
  ]);

  useEffect(() => {
    return () => {
      tearDownTpOverlayChartArtifacts();
    };
  }, [tearDownTpOverlayChartArtifacts]);

  return {
    cleanup,
  };
};
